import React, { useState, useRef, useCallback, useEffect } from "react";
import { makeStyles, Typography, Button, IconButton, CircularProgress } from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import DesktopWindowsOutlinedIcon from "@material-ui/icons/DesktopWindowsOutlined";
import { gsap } from "gsap";

import Layout from "../components/layout/layout";
import theme from "../utils/theme";
import iPhoneFrame from "../assets/images/iPhone 15 Pro Max - Natural Titanium - Portrait.png";

// Document validation constants
const MAX_DOCUMENTS = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

function waitForFlutter(iframe) {
  return new Promise((resolve) => {
    const handleMessage = (event) => {
      if (event.data?.type === 'flutterReady') {
        window.removeEventListener('message', handleMessage);
        resolve();
      }
    };
    window.addEventListener('message', handleMessage);

    // Send ping in case Flutter is already ready
    iframe.contentWindow.postMessage({ sender: 'isFlutterReady' }, '*');
  });
}

const useStyles = makeStyles(() => ({
  pageContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "0 24px 120px",
    alignItems: "start",
    minHeight: "calc(100vh - 440px)",
    [theme.breakpoints.down(900)]: {
      display: "none",
    },
  },
  mobileMessage: {
    display: "none",
    [theme.breakpoints.down(900)]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      textAlign: "center",
      padding: "40px 32px 450px",
    },
  },
  mobileMessageIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    background: "linear-gradient(135deg, #0c64ce 0%, #1976d2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 28,
    boxShadow: "0 8px 32px rgba(12, 100, 206, 0.25), 0 2px 8px rgba(12, 100, 206, 0.15)",
    animation: "$float 3s ease-in-out infinite",
    "& svg": {
      fontSize: 36,
      color: "#fff",
    },
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-6px)",
    },
  },
  mobileMessageTitle: {
    fontSize: "1.375rem",
    fontWeight: 700,
    color: "#0b365d",
    marginBottom: 10,
    letterSpacing: "-0.01em",
  },
  mobileMessageText: {
    fontSize: "0.9375rem",
    color: "#64748b",
    maxWidth: 280,
    lineHeight: 1.6,
  },
  contentWrapper: {
    display: "flex",
    gap: 40,
    alignItems: "start",
    width: "100%",
    maxWidth: 960,
    justifyContent: "center",
    [theme.breakpoints.down(900)]: {
      flexDirection: "column",
      gap: 32,
    },
  },
  formSection: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    width: 640,
    flexShrink: 0,
    [theme.breakpoints.down(900)]: {
      width: "100%",
      maxWidth: 520,
    },
  },
  headerSection: {
    marginBottom: 8,
    textAlign: "center",
    [theme.breakpoints.down(900)]: {
      textAlign: "left",
    },
  },
  title: {
    fontSize: "2.75rem",
    fontWeight: 700,
    color: "#1a1a2e",
    marginBottom: 12,
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "1.1rem",
    color: "#64748b",
    maxWidth: 480,
    margin: "0 auto",
    lineHeight: 1.5,
    [theme.breakpoints.down(900)]: {
      margin: 0,
    },
  },
  inputWrapper: {
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    borderRadius: 20,
    border: "2px solid #e2e8f0",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      boxShadow: "0 4px 24px rgba(12, 100, 206, 0.12), 0 0 0 4px rgba(12, 100, 206, 0.08)",
    },
  },
  textArea: {
    width: "100%",
    minHeight: 100,
    maxHeight: 300,
    padding: 20,
    fontSize: 16,
    fontFamily: "inherit",
    border: "none",
    borderRadius: "20px 20px 0 0",
    resize: "none",
    outline: "none",
    background: "transparent",
    boxSizing: "border-box",
    lineHeight: 1.6,
    overflowY: "auto",
    "&::placeholder": {
      color: "#94a3b8",
    },
  },
  inputActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px 12px 12px",
    flexShrink: 0,
  },
  attachButton: {
    color: "#94a3b8",
    width: 40,
    height: 40,
    transition: "all 0.2s ease",
    "&:hover": {
      color: theme.palette.primary.main,
      background: "rgba(12, 100, 206, 0.08)",
    },
  },
  generateButton: {
    position: "relative",
    background: "linear-gradient(135deg, #0c64ce 0%, #1976d2 100%)",
    color: "#fff",
    padding: "10px 28px",
    borderRadius: 24,
    fontWeight: 600,
    textTransform: "none",
    fontSize: 15,
    minWidth: 150,
    boxShadow: "0 2px 8px rgba(12, 100, 206, 0.3)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    "&:hover": {
      background: "linear-gradient(135deg, #0a56b3 0%, #1565c0 100%)",
      boxShadow: "0 4px 12px rgba(12, 100, 206, 0.4)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      background: "#e2e8f0",
      color: "#94a3b8",
      boxShadow: "none",
      transform: "none",
    },
  },
  generateButtonLoading: {
    background: "linear-gradient(135deg, #0c64ce 0%, #1976d2 100%)",
    boxShadow: "0 2px 12px rgba(12, 100, 206, 0.35)",
    "&:disabled": {
      background: "linear-gradient(135deg, #0c64ce 0%, #1976d2 100%)",
      color: "#fff",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "200%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
      animation: "$shimmer 1.5s infinite",
    },
  },
  "@keyframes shimmer": {
    "0%": {
      transform: "translateX(0)",
    },
    "100%": {
      transform: "translateX(100%)",
    },
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    position: "relative",
    zIndex: 1,
  },
  spinner: {
    color: "#fff",
  },
  fileInput: {
    display: "none",
  },
  attachedFiles: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  fileChip: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#f1f5f9",
    padding: "8px 14px",
    borderRadius: 24,
    fontSize: 13,
    color: "#475569",
    fontWeight: 500,
    "& svg:first-child": {
      fontSize: 16,
      color: "#64748b",
    },
  },
  removeFile: {
    padding: 2,
    marginLeft: 4,
    color: "#94a3b8",
    opacity: 1,
    width: 22,
    transition: "opacity 0.2s ease, width 0.2s ease, margin 0.2s ease, color 0.2s ease, background 0.2s ease",
    "&:hover": {
      color: "#ef4444",
      background: "rgba(239, 68, 68, 0.1)",
    },
    "& svg": {
      fontSize: 14,
    },
  },
  previewSection: {
    position: "relative",
    width: 0,
    opacity: 0,
    overflow: "hidden",
    flexShrink: 0,
    [theme.breakpoints.down(900)]: {
      width: "100%",
      maxWidth: 400,
      opacity: 1,
      overflow: "visible",
    },
  },
  iPhoneContainer: {
    position: "relative",
    width: 390,
    [theme.breakpoints.down(900)]: {
      width: "100%",
      maxWidth: 390,
    },
  },
  iPhoneFrame: {
    width: "100%",
    height: "auto",
    display: "block",
    position: "relative",
    zIndex: 2,
    pointerEvents: "none",
  },
  iPhoneScreen: {
    position: "absolute",
    top: "2.8%",
    left: "7.5%",
    width: "85%",
    height: "94%",
    borderRadius: 40,
    overflow: "hidden",
    background: "#f8fafc",
    zIndex: 1,
  },
  previewIframe: {
    width: "125%",
    height: "125%",
    transform: "scale(0.8)",
    transformOrigin: "top left",
    border: "none",
    borderRadius: 50,
    overscrollBehavior: "contain",
  },
  dropOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(12, 100, 206, 0.06)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  dropOverlayText: {
    background: "#fff",
    padding: "20px 40px",
    borderRadius: 16,
    fontWeight: 600,
    color: theme.palette.primary.main,
    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
    fontSize: 18,
    border: `2px dashed ${theme.palette.primary.main}`,
  },
  fileError: {
    color: "#dc2626",
    fontSize: 13,
    marginTop: 4,
    padding: "0 4px",
  },
}));

const Generate = ({ path }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const previewRef = useRef(null);
  const textAreaRef = useRef(null);
  const iframeRef = useRef(null);
  const isFlutterReadyRef = useRef(false);

  const [prompt, setPrompt] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [previewUrl] = useState(
    process.env.GATSBY_ISDEVELOPMENT === "true"
      ? "https://slate.dev.slatepages.com/#/"
      : "https://slate.slatepages.com/#/"
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [fileError, setFileError] = useState(null);

  const handleFileSelect = useCallback((files) => {
    setFileError(null);
    const filesArray = Array.from(files);

    // Check if adding these files would exceed the limit
    const totalFiles = attachedFiles.length + filesArray.length;
    if (totalFiles > MAX_DOCUMENTS) {
      setFileError(`Maximum ${MAX_DOCUMENTS} documents allowed. You have ${attachedFiles.length} attached.`);
      return;
    }

    const validFiles = [];
    const errors = [];

    for (const file of filesArray) {
      // Validate MIME type
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported file type. Supported: PDF, PNG, JPEG, GIF, and WebP.`);
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds 10 MB limit.`);
        continue;
      }

      validFiles.push({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        file,
      });
    }

    if (errors.length > 0) {
      setFileError(errors.join(' '));
    }

    if (validFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...validFiles]);
    }
  }, [attachedFiles.length]);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    // Reset to auto to measure true content height
    textarea.style.height = 'auto';

    // Set height to content height (CSS maxHeight will cap it)
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, []);

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) {
      handleFileSelect(e.target.files);
    }
    e.target.value = "";
  };

  const handleRemoveFile = (fileId) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Page-level drag and drop handling
  useEffect(() => {
    let dragCounter = 0;

    const handleDragEnter = (e) => {
      e.preventDefault();
      dragCounter++;
      if (dragCounter === 1) {
        setIsDragOver(true);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter === 0) {
        setIsDragOver(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      dragCounter = 0;
      setIsDragOver(false);
      if (e.dataTransfer.files?.length) {
        handleFileSelect(e.dataTransfer.files);
      }
    };

    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("drop", handleDrop);

    return () => {
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("drop", handleDrop);
    };
  }, [handleFileSelect]);

  // Prevent parent scroll when mouse is inside preview iframe
  useEffect(() => {
    const previewContainer = previewRef.current;
    if (!previewContainer) return;

    const handleMouseEnter = () => {
      document.body.style.overflow = 'hidden';
    };

    const handleMouseLeave = () => {
      document.body.style.overflow = '';
    };

    previewContainer.addEventListener('mouseenter', handleMouseEnter);
    previewContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      previewContainer.removeEventListener('mouseenter', handleMouseEnter);
      previewContainer.removeEventListener('mouseleave', handleMouseLeave);
      document.body.style.overflow = '';
    };
  }, [hasGenerated]);

  // Listen for messages from the Flutter iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'flutterReady') {
        isFlutterReadyRef.current = true;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const sendSlateToDemo = async (slateJson) => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }

    if (!isFlutterReadyRef.current) {
      await waitForFlutter(iframeRef.current);
      isFlutterReadyRef.current = true;
    }

    const message = {
      sender: 'loadDemoSlate',
      message: slateJson
    };
    iframeRef.current.contentWindow.postMessage(message, '*');
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const generateSlate = async (description, files) => {
    const baseUrl = process.env.GATSBY_GENERATE_API_BASE || 'http://localhost:5161/api/quick-generate-slate';

    // Convert files to documents format
    const documents = await Promise.all(
      files.map(async (fileObj) => ({
        Content: await fileToBase64(fileObj.file),
        FileName: fileObj.file.name,
        MimeType: fileObj.file.type,
      }))
    );

    const response = await fetch(`${baseUrl}/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        description,
        documents: documents.length > 0 ? documents : undefined,
      }),
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json();
  };

  const showPreview = () => {
    if (!hasGenerated) {
      setHasGenerated(true);

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.to(formRef.current, {
        width: 520,
        duration: 0.6,
      })
      .to(previewRef.current, {
        width: 400,
        opacity: 1,
        duration: 0.6,
      }, 0);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim() && attachedFiles.length === 0) return;

    setIsRequestPending(true);
    setFileError(null);

    let slateData = null;
    try {
      const response = await generateSlate(prompt, attachedFiles);
      slateData = response.translation;
    } catch (error) {
      // Request failed
    } finally {
      setIsRequestPending(false);
    }

    if (slateData) {
      sendSlateToDemo(slateData);
    }

    showPreview();
  };


  return (
    <Layout
      SeoProps={{ title: "Generate Slate" }}
      HeaderProps={{ path }}
      compactFooter
    >
      {isDragOver && (
        <div className={classes.dropOverlay}>
          <span className={classes.dropOverlayText}>Drop files here</span>
        </div>
      )}
      <div className={classes.mobileMessage}>
        <div className={classes.mobileMessageIcon}>
          <DesktopWindowsOutlinedIcon />
        </div>
        <Typography className={classes.mobileMessageTitle}>
          Desktop Experience
        </Typography>
        <Typography className={classes.mobileMessageText}>
          The Slate generator is optimized for desktop browsers. Visit on a computer for the best experience.
        </Typography>
      </div>
      <div className={classes.pageContainer} style={hasGenerated ? { paddingBottom: 300 } : undefined}>
        <div className={classes.contentWrapper}>
          <div
            ref={formRef}
            className={classes.formSection}
          >
            <div className={classes.headerSection}>
              <Typography variant="h1" className={classes.title}>
                Generate a Slate
              </Typography>
              <Typography className={classes.subtitle}>
                Describe what you need and SlateMate will build it for you.
              </Typography>
            </div>

            <div
              className={classes.inputWrapper}
            >
              <textarea
                ref={textAreaRef}
                className={classes.textArea}
                placeholder="Describe your Slate... e.g., 'A maintenance checklist for HVAC systems with photo capture and signature fields'"
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  adjustTextareaHeight();
                }}
              />
              <div className={classes.inputActions}>
                <IconButton
                  className={classes.attachButton}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <AttachFileIcon />
                </IconButton>
                <Button
                  className={`${classes.generateButton} ${isRequestPending ? classes.generateButtonLoading : ''}`}
                  onClick={handleGenerate}
                  disabled={isRequestPending || (!prompt.trim() && attachedFiles.length === 0)}
                >
                  <span className={classes.buttonContent}>
                    {isRequestPending && (
                      <CircularProgress size={16} thickness={3} className={classes.spinner} />
                    )}
                    {isRequestPending ? "Generating" : "Generate"}
                  </span>
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className={classes.fileInput}
                onChange={handleFileInputChange}
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
              />
            </div>

            {attachedFiles.length > 0 && (
              <div className={classes.attachedFiles}>
                {attachedFiles.map((file) => (
                  <div key={file.id} className={classes.fileChip}>
                    <InsertDriveFileIcon />
                    {file.name}
                    <IconButton
                      className={classes.removeFile}
                      onClick={() => handleRemoveFile(file.id)}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}

            {fileError && (
              <Typography className={classes.fileError}>
                {fileError}
              </Typography>
            )}

            </div>

          <div
            ref={previewRef}
            className={classes.previewSection}
          >
            <div className={classes.iPhoneContainer}>
              <img
                src={iPhoneFrame}
                alt="iPhone frame"
                className={classes.iPhoneFrame}
              />
              <div className={classes.iPhoneScreen}>
                <iframe
                  ref={iframeRef}
                  src={previewUrl}
                  className={classes.previewIframe}
                  title="Slate Preview"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Generate;
